/* eslint-disable max-lines */
import { CircularProgress } from '@material-ui/core';
import { getColor } from 'common/constants/_colors';
import { TRACKING } from 'common/constants/constants';
import { LINE_HEIGHT, SIZES, SPACES } from 'common/constants/sizes';
import * as TrackingService from 'common/services/tracking.service';
import { OAuthProviders } from 'common/utils/assets.utils';
import { isTeamByLicense, navigateBookDemoClick } from 'common/utils/utils';
import EmptyData from 'components/EmptyData';
import Icon from 'components/base/Icon';
import Text from 'components/base/Text';
import { Flex, Margin } from 'components/base/layout-components';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import flyingFly from '../../images/icons/flying-fly.svg';
import { talkToUs } from './repositories-modal.service';
import { LBButton } from '@linearb/buzz';

export const Footer = styled(Flex)`
  ${({ isOnboarding }) =>
    isOnboarding
      ? `
    padding-bottom:${SPACES.small};
    padding-left:${SPACES.medium};
    padding-right:${SPACES.medium};`
      : `padding: ${SPACES.medium}; padding-top:${SPACES['no-space']};`}
`;

export const LinkCopied = () => (
  <Flex direction='column'>
    <Text size='large' color='white'>
      Invitation link copied
    </Text>
  </Flex>
);

const UseAccessToken = props => (
  <CtaBox
    {...props}
    imageName='StarsBox'
    text='Change authorization'
    subtext='Use a personal access token or OAuth'
  />
);

const InviteYourAdmin = ({ onClick, ...props }) => {
  const [isClicked, setIsClicked] = useState(false);
  const onClickHandler = () => {
    setIsClicked(true);
    onClick();
    setTimeout(() => setIsClicked(false), 5000);
  };
  return (
    <CtaBox
      {...props}
      withFocusedEffect={isClicked}
      onClick={() => onClickHandler()}
      imageName={isClicked ? 'EnvelopeWithCheckMark' : 'Envelope'}
      text='Invite your Git admin'
      subtext='Send an invitation link to<br />your admin to get access'
    />
  );
};

const TalkToUs = props => (
  <CtaBox
    {...props}
    imageName='HumanBubble'
    text='Talk to a human'
    subtext='Chat now<br />or schedule a call with us'
  />
);

const RenderHTML = props => (
  <span dangerouslySetInnerHTML={{ __html: props.html }}></span>
);

const CtaBox = ({ imageName, text, subtext, ...rest }) => (
  <CtaBoxWrapper
    {...rest}
    width='14rem'
    height='14rem'
    padding='small'
    margin='small'
    justify='center'
    content='center'
    items='center'
    direction='column'
  >
    <Icon size='x-large' name={imageName} color='pigeonPost' />
    <Text size='medium' pt='1rem' pb='1rem'>
      {text}
    </Text>
    <Text size='small' color='shuttleGray' align='center'>
      <RenderHTML html={subtext} />
    </Text>
  </CtaBoxWrapper>
);

export const UnableToFindRepositories = ({
  provider = '',
  withTitleAndBoxes,
  isTeamLicense,
  accessTokenCb = _.noop,
  inviteYourAdminCb = _.noop,
  isOnboarding
}) => {
  const Logo = OAuthProviders[provider.toLowerCase()]?.logo;
  useEffect(() => {
    if (isOnboarding) {
      TrackingService.logEvent(TRACKING.EVENTS.VISIT, {
        page: TRACKING.PAGES.ONBOARDING,
        page_tab: TRACKING.PAGE_TABS.UNABLE_TO_FIND_REPOS
      });
    }
  }, []);

  return (
    <Flex justify='center' direction='column'>
      {withTitleAndBoxes ? (
        <Flex direction='row' items='center' margin='medium' content='center'>
          <Logo className='size-8' />
          <Text size='x-x-x-large' pl='1rem' weight='300'>
            {isTeamLicense
              ? 'Select repositories for your organization'
              : 'Select repositories'}
          </Text>
        </Flex>
      ) : (
        <Margin b='medium' />
      )}
      <Flex
        direction='column'
        items='center'
        content='center'
        mb='x-big'
        {...(isOnboarding ? { margin: 'medium' } : {})}
      >
        <img src={flyingFly} alt='' />
        <Text
          size='x-x-large'
          weight='300'
          color='shuttleGray'
          pt='1rem'
          pb='3rem'
        >
          We were unable to find any active team repositories
        </Text>
        {withTitleAndBoxes && (
          <>
            <Text size='medium' weight='300' color='shuttleGray'>
              Here's what you can do:
            </Text>
            <RepositoriesModalBoxes
              accessTokenCb={accessTokenCb}
              inviteYourAdminCb={inviteYourAdminCb}
            />
          </>
        )}
      </Flex>
    </Flex>
  );
};

const FullWidthCircularProgress = styled(CircularProgress)`
  width: 100%;
`;
FullWidthCircularProgress.displayName = 'FullWidthCircularProgress';

export const LoadingTemplate = ({ text }) => (
  <Flex
    direction='column'
    justify='center'
    items='center'
    height='100%'
    width='100%'
  >
    <Text size='x-x-x-large' weight='300' color='shuttleGray' pb='3rem'>
      {text}
    </Text>
    <FullWidthCircularProgress />
  </Flex>
);

export const LimitExceeded = ({ maxRepos }) => (
  <Flex
    direction='column'
    justify='center'
    items='center'
    height='100%'
    width='100%'
  >
    <Text size='x-x-x-large' weight='300' color='shuttleGray'>
      You have reached your limit of {maxRepos} repositories.
    </Text>
  </Flex>
);

export const ServerError = ({ info }) => (
  <EmptyData margin='auto' title='Oops, something went wrong' info={info} />
);

export const InsufficientPermissions = ({
  onClick,
  findAndDeleteSensor,
  isOnboarding,
  openReAuthModal
}) => (
  <Flex
    direction='column'
    justify='center'
    items='center'
    height='100%'
    width='100%'
  >
    <Text
      size='x-x-x-large'
      weight='300'
      color='shuttleGray'
      pb={SPACES['x-medium']}
    >
      We were not able to access your Git account
    </Text>
    <Text size='medium' pb={SPACES['x-medium']} lineHeight={LINE_HEIGHT.medium}>
      This might be because you don’t have the right permissions in the
      <br />
      account or you were not granted team access.{' '}
      <LBButton variant='ghost' onClick={onClick}>
        learn more &gt;
      </LBButton>
    </Text>
    <LBButton
      size='lg'
      onClick={
        isOnboarding ? () => findAndDeleteSensor() : () => openReAuthModal()
      }
    >
      {isOnboarding ? 'Try again' : 'Reauthorize Git'}
    </LBButton>
  </Flex>
);

export const Badge = styled(Flex)`
  border-radius: 2px;
  padding: 0.3rem 0.5rem;
  font-size: ${SIZES.small};
  align-items: center;
  ${({ type }) =>
    type === 'active'
      ? `
    border: 1px solid ${getColor('madang')};
    background-color: ${getColor('fetaLight')};
    color: ${getColor('funGreen')};
  `
      : `
    border: 1px solid ${getColor('catskillWhite')};
    color: ${getColor('silverSand')};
  `}
`;

const RepositoriesModalBoxes = ({
  accessTokenCb = _.noop,
  inviteYourAdminCb = _.noop
}) => (
  <Flex direction='row' items='center' content='center'>
    <UseAccessToken onClick={() => accessTokenCb()} />
    <InviteYourAdmin onClick={() => inviteYourAdminCb()} />
    <TalkToUs onClick={() => talkToUs()} />
  </Flex>
);

const CtaBoxWrapper = styled(Flex)`

  border: 1px solid ${getColor('botticelli')};
  border-radius: 4px;
  color: ${({ withFocusedEffect }) =>
    withFocusedEffect ? getColor('pastelGreen') : getColor('shuttleGray')};

  svg {
    path[stroke] {
      stroke: ${({ withFocusedEffect }) =>
        withFocusedEffect ? getColor('pastelGreen') : getColor('pigeonPost')};
    }

    path[fill] {
      stroke: ${({ withFocusedEffect }) =>
        withFocusedEffect ? getColor('pastelGreen') : getColor('pigeonPost')};
    }
  }

  &:hover {
    cursor: pointer;

    ${({ withFocusedEffect }) =>
      withFocusedEffect
        ? ''
        : `
      border: 1px solid ${getColor('dodgerBlue')};
      background-color: ${getColor('zircon')};
      color: ${getColor('dodgerBlue')};

      p {
        color: ${getColor('dodgerBlue')} !important;
      }
      svg {
        path[stroke] {
          stroke: ${getColor('dodgerBlue')} !important;
        }
        path[fill] {
          fill: ${getColor('dodgerBlue')} !important;
        }
        rect[stroke] {
          stroke: ${getColor('dodgerBlue')} !important;
        }
        rect[fill] {
          fill: ${getColor('dodgerBlue')} !important;
        }
      }`}
`;

export const Subtitle = ({ isOnboarding, license, isConnectGitStreamFlow }) => {
  const upgrade = isTeamByLicense(license) ? (
    <LBButton variant='ghost' onClick={navigateBookDemoClick}>
      Upgrade for more
    </LBButton>
  ) : (
    ''
  );

  return isConnectGitStreamFlow ? (
    <Text size='medium' color='shuttleGray'>
      Make sure to select the cm repo to allow gitStream automations in your
      repos.
    </Text>
  ) : isOnboarding ? (
    <Text size='medium' lineHeight='1.8rem'>
      We’ve selected what look like the most relevant repos to connect.
      <br />
      We recommend connecting all repos with recent activity.
    </Text>
  ) : (
    <>
      <Text size='medium' color='shuttleGray' pb='0.4rem'>
        To see the LinearB magic, select active team repos with multiple
        contributors and recent Pull Requests.
      </Text>
      <Text size='medium' color='shuttleGray'>
        Select repositories now. You can change them later.{' '}
        {!isOnboarding ? upgrade : null}
      </Text>
    </>
  );
};
